<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="layers">
  <b-message v-if="error" type="is-danger" has-icon icon-size="is-small" size="is-small">
    <p> {{ $t('unexpected-error-info-message') }} </p>
  </b-message>
  <template v-else>
    <!-- UI-8b: compact add-layer row -->
    <div class="lp-add">
      <!-- Keyed by layer.id, not by the layer object.
           A DOM <option value> is a scalar; binding the object meant the value could only be set
           by picking the exact option element, so the control could not be driven by anything
           that sets a value by string - test tooling and assistive tech alike. The id
           round-trips through the DOM, and addLayerById already existed to resolve it. -->
      <b-select :placeholder="$t('select-layer')" size="is-small" v-model="selectedLayerId" expanded>
        <option v-for="layer in unselectedLayers" :value="layer.id" :key="layer.id">
          {{ layerName(layer) }}
        </option>
      </b-select>
      <button
        class="lp-add-btn"
        @click="addSelectedLayer()"
        :disabled="selectedLayerId === null"
        :title="$t('button-add')"
      >
        <i class="fas fa-plus"></i>
      </button>
    </div>

    <!-- UI-8b: collapsible compact list of selected layers -->
    <button v-if="selectedLayers.length" class="lp-list-head" @click="layersCollapsed = !layersCollapsed">
      <i class="fas lp-caret" :class="layersCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
      <span class="lp-count">{{ selectedLayers.length }}</span>
    </button>

    <div v-show="!layersCollapsed" class="lp-list">
      <div v-for="(layer, idx) in selectedLayers" :key="layer.id" class="lp-row">
        <button class="lp-ico lp-eye" :class="{off: !layer.visible}" @click="toggleLayerVisibility(idx)" :title="$t('show')">
          <i class="far" :class="layer.visible ? 'fa-eye' : 'fa-eye-slash'"></i>
        </button>
        <span class="lp-name" :title="layerName(layer)">{{ layerName(layer) }}</span>
        <button v-if="!reviewMode" class="lp-ico lp-draw" :class="{on: layer.drawOn}"
                :disabled="!canDraw(layer)" @click="toggleLayerDrawOn(idx)" :title="$t('draw')">
          <i class="fas fa-pencil-alt"></i>
        </button>
        <button v-if="!reviewMode || !layer.isReview" class="lp-ico lp-remove" @click="removeLayer(idx)" :title="$t('button-remove')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div v-if="selectedLayers.length === 0" class="lp-empty">{{$t('no-selected-layers')}}</div>
    </div>

    <div class="opacity">
      <label>{{ $t('layers-opacity') }}</label>
      <input class="slider is-fullwidth is-small" v-model="layersOpacity" step="0.05" min="0" max="1" type="range">
    </div>
  </template>
</div>
</template>

<script>
import _ from 'lodash';
import {get} from '@/utils/store-helpers';
import {Ims, ProjectDefaultLayerCollection} from '@/api';

export default {
  name: 'layers-panel',
  props: {
    index: String,
    layersToPreload: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      error: false,

      layers: [], // Array<User> (representing user layers)
      indexLayers: [],
      selectedLayerId: null,
      layersCollapsed: false // UI-8b: collapse the selected-layers list
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    project: get('currentProject/project'),
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    activePanel() {
      return this.imageWrapper.activePanel;
    },
    layersOpacity: {
      get() {
        return this.imageWrapper.style.layersOpacity;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setLayersOpacity', Number(value));
      }
    },
    selectedLayers() { // Array<User> (representing user layers)
      return this.imageWrapper.layers.selectedLayers || [];
    },
    selectedLayersIds() {
      return this.selectedLayers.map(layer => layer.id);
    },
    unselectedLayers() {
      return this.layers.filter(layer => !this.selectedLayersIds.includes(layer.id)).sort((a, b) => (a.fullName < b.fullName) ? -1 : 1);
    },
    nbReviewedAnnotations() {
      return this.indexLayers.reduce((cnt, layer) => cnt + layer.countReviewedAnnotation, 0);
    },
    hasReviewLayer() {
      return this.image.inReview || this.image.reviewed;
    },
    reviewLayer() {
      return {id: -1, isReview: true};
    },
    reviewMode() {
      return this.imageWrapper.review.reviewMode;
    },
    isActiveImage() {
      return this.viewerWrapper.activeImage === this.index;
    }
  },
  watch: {
    activePanel() {
      this.fetchIndexLayers();
    },
    reviewMode() {
      if (this.reviewMode) {
        if (!this.layers.includes(this.reviewLayer)) {
          this.layers.push(this.reviewLayer);
        }
        this.addLayer(this.reviewLayer);
      } else {
        if (!this.hasReviewLayer) {
          let index = this.selectedLayersIds.findIndex(id => id === this.reviewLayer.id);
          if (index !== -1) {
            this.removeLayer(index);
          }

          this.layers = this.layers.filter(layer => !layer.isReview);
        }
      }
    },
    layersToPreload: {
      deep: true,
      handler: function (layersToPreload) {
        layersToPreload.forEach(layerId => {
          let index = this.selectedLayersIds.findIndex(id => id === this.reviewLayer.id);
          if (index !== -1) {
            if (!this.selectedLayers[index].visible) {
              this.toggleLayerVisibility(index);
            }
            return;
          }

          this.addLayerById(layerId, true);
        });
      }
    }
  },
  methods: {
    addAnnotationEventHandler(annot, saved = true) {
      this.annotationEventHandler(annot);
      let updatedProject = this.$store.state.currentProject.project.clone();

      if (annot.type === 'UserAnnotation') {
        if (saved) {
          updatedProject.numberOfAnnotations++;
        }
      } else {
        updatedProject.numberOfReviewedAnnotations++;
      }

      this.$store.dispatch('currentProject/updateProject', updatedProject);
    },
    deleteAnnotationEventHandler(annot) {
      this.annotationEventHandler(annot);
    },
    annotationEventHandler(annot) {
      if (annot.image === this.image.id) {
        this.fetchIndexLayers();
      }
    },
    reloadAnnotationsHandler({idImage} = {}) {
      if (!idImage || idImage === this.image.id) {
        this.fetchIndexLayers();
      }
    },

    layerName(layer) {
      if (!Object.prototype.hasOwnProperty.call(layer, 'username')) {
        return layer.name;
      }

      if (layer.isReview) {
        return `${this.$t('review-layer')} (${this.nbReviewedAnnotations})`;
      }

      let id = (this.currentAccount.isDeveloper) ? ` (${this.$t('id')}: ${layer.id})` : '';

      let indexLayer = this.indexLayers.find(index => index.user === layer.id) || {};
      return `${layer.name}${id} (${indexLayer.countAnnotation || 0})`;
    },

    canDraw(layer) {
      return !layer.isReview && this.$store.getters['currentProject/canEditLayer'](layer.id);
    },

    addLayerById(id, visible) {
      let layer = this.layers.find(layer => layer.id === id);
      if (layer) {
        this.addLayer(layer, visible);
      }
    },

    /** The add button: resolve the chosen id to its layer, then add it. */
    addSelectedLayer() {
      if (this.selectedLayerId === null) {
        return;
      }
      this.addLayerById(this.selectedLayerId, true);
      this.selectedLayerId = null;
    },

    addLayer(layer, visible = true) {
      // This used to default to `layer = this.selectedLayer`, which no longer exists now the
      // picker is keyed by id. A zero-arg call would otherwise fail deep inside with
      // "cannot read property 'id' of undefined"; say what is actually wrong instead.
      if (!layer || layer.id === undefined) {
        console.warn('addLayer called without a layer; use addLayerById(id) or addSelectedLayer()');
        return;
      }
      if (this.selectedLayersIds.includes(layer.id)) {
        return;
      }

      layer.visible = visible;
      layer.drawOn = (layer.id === this.currentUser.id && this.canDraw(layer));
      this.$store.dispatch(this.imageModule + 'addLayer', layer);
    },

    removeLayer(index) {
      this.$store.dispatch(this.imageModule + 'removeLayer', index);
    },

    toggleLayerVisibility(index) {
      this.$store.dispatch(this.imageModule + 'toggleLayerVisibility', index);
    },

    toggleLayerDrawOn(index) {
      this.$store.commit(this.imageModule + 'toggleLayerDrawOn', index);
    },

    async fetchLayers() {
      this.layers = (await this.project.fetchUserLayers(this.image.id)).array;
      if (this.hasReviewLayer) {
        this.layers.push(this.reviewLayer);
      }

      let layers = (await Ims.instance.api.get(`image-instances/${this.image.id}/annotation-layers`)).data;
      this.layers.push(...layers);

      // if image instance was changed (e.g. with previous/next image navigation), some of the selected layers
      // may not be relevant for the current image => filter them
      let idLayers = this.layers.map(layer => layer.id);
      this.$store.commit(this.imageModule + 'filterSelectedLayers', idLayers);
    },

    async fetchIndexLayers(force = false) {
      if (!force && this.activePanel !== 'layers') {
        return;
      }
      // TODO: optimize, backend should be able to send indexes for several slices at once.
      let indexLayers = await Promise.all(this.slices.map(async slice => await slice.fetchAnnotationsIndex()));
      indexLayers = Object.values(_.groupBy(indexLayers.flat(), 'user'));
      this.indexLayers = indexLayers.map(userIndexLayers => userIndexLayers.reduce((a, b) => {
        return {
          user: a.user,
          countAnnotation: a.countAnnotation + b.countAnnotation,
          countReviewedAnnotation: a.countReviewedAnnotation + b.countReviewedAnnotation
        };
      }, {user: userIndexLayers[0].user, countAnnotation: 0, countReviewedAnnotation: 0}));
      // ----
    },

    shortkeyHandler(key) {
      if (!key.startsWith('toggle-all-') && !this.isActiveImage) { // shortkey should only be applied to active map
        return;
      }

      key = key.replace('toggle-all-', 'toggle-');
      if (key === 'toggle-selected-layers') {
        this.selectedLayers.forEach((layer, index) => this.toggleLayerVisibility(index));
      } else if (key === 'toggle-review-layer') {
        let index = this.selectedLayersIds.findIndex(id => id === this.reviewLayer.id);
        if (index !== -1) {
          this.toggleLayerVisibility(index);
          return;
        }

        if (this.layers.includes(this.reviewLayer)) {
          this.addLayer(this.reviewLayer);
        }
      }
    }
  },
  async created() {
    try {
      await Promise.all([
        this.fetchLayers(),
        this.fetchIndexLayers(true),
      ]);
    } catch (error) {
      console.log(error);
      this.error = true;
      this.$notify({type: 'error', text: this.$t('notif-error-loading-annotation-layers')});
      return;
    }

    let layersToAdd = this.layersToPreload.map(id => ({id, visible: true}));

    if (!this.imageWrapper.layers.selectedLayers) { // we do not use computed property selectedLayers because we don't want the replacement by [] if the store array is null
      if (!this.layersToPreload.includes(this.currentUser.id)) {
        layersToAdd.push({id: this.currentUser.id, visible: true});
      }

      try {
        let defaultLayers = await ProjectDefaultLayerCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.project.id
        });

        let addedIds = layersToAdd.map(layer => layer.id);

        defaultLayers.array.forEach(({user, hideByDefault}) => {
          if (!addedIds.includes(user)) {
            layersToAdd.push({id: user, visible: !hideByDefault});
          }
        });
      } catch (error) {
        console.log(error);
      }
    }
    layersToAdd.map(layer => this.addLayerById(layer.id, layer.visible));
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.addAnnotationEventHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationEventHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$on('annotation-layers:refresh', this.fetchLayers);
  },
  beforeDestroy() {
    this.$eventBus.$off('addAnnotation', this.addAnnotationEventHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationEventHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$off('annotation-layers:refresh', this.fetchLayers);
  }
};
</script>

<style scoped>
/* UI-8b: compact, modern Annotation Layers list */
.layers { font-size: 12px; }
.lp-add { display: flex; gap: 6px; margin-bottom: 8px; }
.lp-add ::v-deep .select, .lp-add ::v-deep select { width: 100%; font-size: 12px; }
.lp-add-btn {
  flex: 0 0 30px; height: 30px; border: 1px solid #d4d9e2; border-radius: 7px;
  background: #fff; color: #1E63D0; cursor: pointer;
}
.lp-add-btn:disabled { color: #b5bcca; cursor: not-allowed; }
.lp-list-head {
  display: flex; align-items: center; gap: 6px; width: 100%;
  background: none; border: 0; padding: 2px 0 6px; cursor: pointer;
  color: #5a6172;
}
.lp-caret { font-size: 9px; width: 10px; }
.lp-count { background: #eef1f6; border-radius: 9px; padding: 0 7px; font-size: 10px; color: #5a6172; }
.lp-list { display: flex; flex-direction: column; gap: 2px; }
.lp-row { display: flex; align-items: center; gap: 6px; height: 28px; padding: 0 4px; border-radius: 6px; }
.lp-row:hover { background: #f2f4f8; }
.lp-name { flex: 1; font-size: 12px; color: #0e1116; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lp-ico {
  flex: 0 0 22px; height: 22px; border: 0; background: none; border-radius: 5px;
  color: #8a91a1; cursor: pointer; font-size: 12px; display: inline-flex; align-items: center; justify-content: center;
}
.lp-ico:hover { background: #e7ebf2; color: #5a6172; }
.lp-eye.off { color: #c2c8d2; }
.lp-draw.on { color: #1E63D0; background: #E4ECFB; }
.lp-remove:hover { background: #fbeae9; color: #c8443c; }
.lp-ico:disabled { color: #d3d8e0; cursor: not-allowed; background: none; }
.lp-empty { font-size: 11px; color: #8a91a1; font-style: italic; padding: 4px; }
</style>

<style scoped>
>>> select {
  width: 21em;
}

.table {
  margin-bottom: 1em !important;
  font-size: 0.9em;
}

.table tbody {
  display: block;
  overflow: auto;
  max-height: 10em;
}

.table thead tr {
  display: block;
}

td, th {
  padding: 0.25em !important;
  vertical-align: middle !important;
}

td .button {
  width: 1.5em;
  height: 1.5em;
  font-size: 0.9em;
  padding: 0;
}

.checkbox-column {
  min-width: 2.2em;
  text-align: center !important;
}

.name-column {
  width: 100%;
}

>>> .checkbox .control-label {
  padding: 0 !important;
}

>>> input[type="range"].slider {
  margin: 0;
  padding: 0;
}

.opacity {
  display: flex;
  align-items: center;
}

.opacity label {
  text-transform: uppercase;
  font-size: 0.8em;
  width: 15em;
}
</style>
